import React, { FC, useCallback, useEffect, useState } from 'react';

import { btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import { PolicyNote } from '@r1-frontend/ui-react/components/PolicyNote';

import * as ST from './styled';
export type { TTemplate } from './styled';

declare const grecaptcha: { getResponse: () => string };

export type TField = {
    value: string,
    isValid: boolean,
    clearInput: () => void,
}

export interface ISubmitParams {
    name: TField,
    phone: TField,
    recaptchaToken: string | null,
}

interface IFieldParams {
    placeholder: string,
    errorMessage: string,
}

export type TFieldNames = 'fio' | 'phone';

export interface IPropsNamePhoneFormPhone {
    templateVariant?: ST.TTemplate,
    isOnlyPhone?: boolean,
    recapchaSiteKey?: string,
    btnText?: string | React.ReactNode,
    btnType?: btnTypes,
    isLoading?: boolean,
    isDisabled?: boolean,
    invertColor?: boolean,
    policyPath?: string,
    className?: string,
    onButtonClick: (params: ISubmitParams) => void,
    onChange?: (value: TField, fieldName: TFieldNames) => void,
    phoneParams?: IFieldParams,
    nameParams?: IFieldParams,
}

/**
 *  Форма заказа обратного звонка, с попапом результата (отправку аналитики выполняем снаружи формы)
 * @param {boolean} isOnlyPhone - только телефон
 * @param {TTemplate} templateVariant - варианты расположения полей формы
 * @param {string} recapchaSiteKey - ключ для отображения рекапчи
 * @param {boolean} isLoading - отображение сосотояния загрузки
 * @param {boolean} isDisabled - отображение сосотояния блокировки
 * @param {function} onButtonClick - колбэк при клике на кнопку
 * @param {string/ReactNode} btnText - наименование кнопки
 * @param {boolean} invertColor - инвертирует цвета чекбокса
 * @param policyPath - ссылка на политику конфиденциальности
 */
export const NamePhoneForm:FC<IPropsNamePhoneFormPhone> = ({
    templateVariant = 'first',
    isOnlyPhone = false,
    btnText = 'Отправить',
    btnType = btnTypes.ACCENT,
    isLoading,
    isDisabled,
    invertColor,
    recapchaSiteKey = '',
    policyPath = '/policy.pdf',
    phoneParams,
    nameParams,
    onButtonClick,
    onChange,
    className,
}) => {
    const [phone, setPhone] = useState<TField>();
    const [name, setName] = useState<TField>();
    const [isValidInputs, setIsValidInputs] = useState(false);
    const [isRecaptcha, setIsRecaptcha] = useState(false);
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);

    useEffect(() => {
        setIsRecaptcha(!!recapchaSiteKey);
    }, [recapchaSiteKey]);

    useEffect(() => {
        const isValid = isOnlyPhone ? phone?.isValid : (phone?.isValid && name?.isValid);
        setIsValidInputs(isValid as boolean);
    }, [isOnlyPhone, phone, name]);

    const clickHandler = () => {
        if (isLoading || isDisabled || !isValidInputs) {
            return;
        }

        const recaptchaToken = isRecaptcha ? grecaptcha?.getResponse() : null;

        onButtonClick({
            name: (name as TField),
            phone: (phone as TField),
            recaptchaToken,
        });
    };

    const onChangeHandler = useCallback((
        stateInput: TField,
        fieldName: TFieldNames,
    ) => {
        if (stateInput.value === undefined) {
            return;
        }

        if (fieldName === 'phone') {
            setPhone(stateInput);
        } else {
            setName(stateInput);
        }

        typeof onChange === 'function' && onChange(stateInput, fieldName);
    }, [onChange]);

    return (
        <ST.NamePhoneForm className={className}>
            <ST.GridTemplate variant={templateVariant} isOnlyPhone={isOnlyPhone} isRecaptcha={isRecaptcha}>
                {!isOnlyPhone &&
                    <ST.InputName
                        name='fio'
                        onChange={(stateInput: TField) => onChangeHandler(stateInput, 'fio')}
                        placeholder={nameParams?.placeholder || 'Как вас зовут?'}
                        errorMessage={nameParams?.errorMessage || 'Ожидаются только буквы и дефисы'}
                        disabled={isLoading}
                        data-test='field-name'
                    />
                }
                <ST.InputPhone
                    name='phone'
                    onChange={(stateInput: TField) => onChangeHandler(stateInput, 'phone')}
                    placeholder={phoneParams?.placeholder || 'Номер телефона'}
                    errorMessage={phoneParams?.errorMessage || 'Телефон в формате +7 (999) 999-99-99'}
                    disabled={isLoading}
                    data-test='field-phone'
                />
                <ST.PolicyNoteWrapper invertColor={invertColor}>
                    <PolicyNote
                        data-test='policy-note'
                        agreeUrl={policyPath}
                        buttonText={btnText}
                    />
                </ST.PolicyNoteWrapper>
                <ST.Button
                    type={btnType}
                    onClick={clickHandler}
                    disabled={isDisabled || (isRecaptcha && !captchaToken) || !isValidInputs}
                    isLoading={isLoading}
                    data-test='submit-btn'
                >
                    {btnText}
                </ST.Button>
                {isRecaptcha &&
                    <ST.Captcha
                        sitekey={recapchaSiteKey}
                        onChange={setCaptchaToken}
                    />
                }
            </ST.GridTemplate>
        </ST.NamePhoneForm>
    );
};
